import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Save, Plus, Trash2, PoundSterling, Calendar, Landmark, Building2, Phone, Share2, Package, Check, Tag, GripVertical, HardDrive, RefreshCw } from "lucide-react";
import api from "../../lib/api";

const TABS = [
  { key: "pricing", label: "Pricing & Discounts", icon: PoundSterling },
  { key: "addons", label: "Invoice Add-ons", icon: Package },
  { key: "term", label: "Terms", icon: Calendar },
  { key: "bank", label: "Bank Accounts", icon: Landmark },
  { key: "venue", label: "Venue & Signature", icon: Building2 },
  { key: "contact", label: "Contact", icon: Phone },
  { key: "social", label: "Social", icon: Share2 },
  { key: "lead_statuses", label: "Lead Statuses", icon: Tag },
  { key: "storage", label: "Storage", icon: HardDrive },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("pricing");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", s);
      setS(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setS({ ...s, [k]: v });
  const setNum = (k, v) => setS({ ...s, [k]: parseFloat(v || 0) });

  // Stable ephemeral row IDs — attach once per row to avoid index-as-key bugs when reordering/removing.
  const rowId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const addTerm = () => setS({
    ...s,
    terms: [...(s.terms || []), {
      _key: rowId(),
      term: "New Term", start_date: "", end_date: "",
      bank_name: s.default_bank_name || "", bank_account_name: s.default_bank_account_name || "",
      bank_account_number: s.default_bank_account_number || "", bank_sort_code: s.default_bank_sort_code || "",
    }],
  });
  const updateTerm = (i, k, v) => {
    const terms = [...s.terms]; terms[i] = { ...terms[i], [k]: v }; setS({ ...s, terms });
  };
  const removeTerm = (i) => {
    const terms = [...s.terms]; terms.splice(i, 1); setS({ ...s, terms });
  };

  // --- Invoice addons CRUD ---
  const addons = s?.invoice_addons || [];
  const addAddon = () => setS({ ...s, invoice_addons: [...addons, { _key: rowId(), description: "New pack", unit_price: 0 }] });
  const updateAddon = (i, k, v) => {
    const arr = [...addons];
    arr[i] = { ...arr[i], [k]: k === "unit_price" ? parseFloat(v || 0) : v };
    setS({ ...s, invoice_addons: arr });
  };
  const removeAddon = (i) => {
    const arr = [...addons]; arr.splice(i, 1); setS({ ...s, invoice_addons: arr });
  };

  if (!s) return <div className="text-[#6E6074]">Loading…</div>;

  return (
    <div data-testid="admin-settings-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">site configuration</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Settings</h1>
          <p className="text-sm text-[#6E6074] mt-1">Change your pricing, bank accounts, term calendar, venue and socials — no code required.</p>
        </div>
        <button onClick={save} disabled={saving} className="sac-btn-primary disabled:opacity-60" data-testid="settings-save-btn">
          <Save size={16} /> {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5 border-b border-[#E5DCE6] pb-3">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`settings-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${active ? "bg-[#5D2C8F] text-white shadow" : "bg-white text-[#5D2C8F] border border-[#E5DCE6] hover:bg-[#F7F0F9]"}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="admin-surface p-6 max-w-4xl">
        {tab === "pricing" && (
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Course price (£)" type="number" v={s.course_price} on={(v) => setNum("course_price", v)} testid="settings-price" hint="Full 14-week fee. This is what appears on the public site and on new invoices." />
            <F label="Currency" v={s.currency} on={(v) => set("currency", v)} testid="settings-currency" />
            <F label="Returner discount (£)" type="number" v={s.returner_discount} on={(v) => setNum("returner_discount", v)} testid="settings-returner-discount" hint="Auto-applied when a student is flagged as a Returner." />
            <F label="Deposit amount (£)" type="number" v={s.deposit_amount} on={(v) => setNum("deposit_amount", v)} testid="settings-deposit" hint="Shown as the minimum deposit to secure a place." />
            <F label="SumUp pay link" v={s.sumup_pay_link} on={(v) => set("sumup_pay_link", v)} testid="settings-sumup-link" hint="Full URL — appears on invoices and the website's card-payment button." />
          </div>
        )}

        {tab === "addons" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-display text-xl text-[#5D2C8F]">Invoice add-on catalogue</h3>
                <p className="text-xs text-[#6E6074]">These appear as one-click buttons on every invoice (Starter pack, Paper pack, etc). Edit the price here and it'll be picked up on the next invoice automatically.</p>
              </div>
              <button onClick={addAddon} className="sac-btn-secondary !py-2 !px-3 text-sm" data-testid="settings-add-addon-btn"><Plus size={14} /> Add pack</button>
            </div>

            <div className="space-y-3">
              {addons.map((a, i) => (
                <div key={a._key || a.description || `addon-${i}`} className="border border-[#E5DCE6] rounded-xl p-3 bg-white flex flex-wrap items-end gap-3" data-testid={`settings-addon-row-${i}`}>
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs text-[#5D2C8F]">Name</label>
                    <input value={a.description || ""} onChange={(e) => updateAddon(i, "description", e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid={`settings-addon-name-${i}`} />
                  </div>
                  <div className="w-36">
                    <label className="text-xs text-[#5D2C8F]">Price (£)</label>
                    <input type="number" step="0.01" value={a.unit_price ?? 0} onChange={(e) => updateAddon(i, "unit_price", e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid={`settings-addon-price-${i}`} />
                  </div>
                  <button onClick={() => removeAddon(i)} className="text-red-500 hover:bg-red-50 rounded p-2 mb-1" data-testid={`settings-addon-remove-${i}`} aria-label={`Remove ${a.description}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {addons.length === 0 && (
                <div className="text-sm text-[#6E6074] italic">No invoice add-ons yet — click "Add pack" to create one.</div>
              )}
            </div>
            <p className="text-xs text-[#8A7C92] mt-4">Remember to hit <span className="font-medium">Save all changes</span> — your edits aren't live until you do.</p>
          </div>
        )}

        {tab === "term" && (
          <div className="space-y-6">
            <TermsManager />

            <div className="border-t border-[#E5DCE6] pt-5">
              <h3 className="font-display text-xl text-[#5D2C8F]">Class session times</h3>
              <p className="text-xs text-[#6E6074] leading-snug mt-0.5 mb-3">Change the times displayed on invoices, welcome emails, and the public schedule. Leave a field blank to fall back to the system default.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <F
                  label="Tuesday AM (morning) session time"
                  v={(s.session_times && s.session_times.tue_am) || ""}
                  on={(v) => set("session_times", { ...(s.session_times || {}), tue_am: v })}
                  testid="settings-session-time-tue-am"
                  hint='Default: "10:00 AM – 1:00 PM"'
                />
                <F
                  label="Tuesday PM (afternoon) session time"
                  v={(s.session_times && s.session_times.tue_pm) || ""}
                  on={(v) => set("session_times", { ...(s.session_times || {}), tue_pm: v })}
                  testid="settings-session-time-tue-pm"
                  hint='Default: "2:00 PM – 5:00 PM"'
                />
                <F
                  label="Friday Evening session time"
                  v={(s.session_times && s.session_times.fri_eve) || ""}
                  on={(v) => set("session_times", { ...(s.session_times || {}), fri_eve: v })}
                  testid="settings-session-time-fri-eve"
                  hint='Default: "6:00 PM – 8:30 PM"'
                />
              </div>
            </div>
          </div>
        )}

        {tab === "bank" && (
          <div>
            <h3 className="font-display text-xl text-[#5D2C8F] mb-1">Default bank account</h3>
            <p className="text-xs text-[#6E6074] mb-3">Used when a term doesn't have a specific override. All invoices for that term will quote these details.</p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <F label="Institution name" v={s.default_bank_name} on={(v) => set("default_bank_name", v)} testid="settings-default-bank-name" />
              <F label="Account holder" v={s.default_bank_account_name} on={(v) => set("default_bank_account_name", v)} testid="settings-default-bank-holder" />
              <F label="Account number" v={s.default_bank_account_number} on={(v) => set("default_bank_account_number", v)} testid="settings-default-bank-number" />
              <F label="Sort code" v={s.default_bank_sort_code} on={(v) => set("default_bank_sort_code", v)} testid="settings-default-bank-sort" />
            </div>

            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-display text-xl text-[#5D2C8F]">Per-term overrides</h3>
                <p className="text-xs text-[#6E6074]">e.g. Summer 2026 → Starling, Autumn 2026 → SumUp. Invoices auto-pick the right one by term.</p>
              </div>
              <button onClick={addTerm} className="sac-btn-secondary !py-2 !px-3 text-sm" data-testid="settings-add-term-btn"><Plus size={14} /> Add term</button>
            </div>

            <div className="space-y-4">
              {(s.terms || []).map((t, i) => (
                <div key={t._key || t.term || `term-${i}`} className="border border-[#E5DCE6] rounded-xl p-4 bg-white" data-testid={`settings-term-row-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-display text-lg text-[#5D2C8F]">{t.term || "Untitled term"}</div>
                    <button onClick={() => removeTerm(i)} className="text-red-500 hover:bg-red-50 rounded p-1" data-testid={`settings-term-remove-${i}`}><Trash2 size={16} /></button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <F label="Term label" v={t.term} on={(v) => updateTerm(i, "term", v)} />
                    <div className="grid grid-cols-2 gap-2">
                      <F label="Start date" type="date" v={t.start_date || ""} on={(v) => updateTerm(i, "start_date", v)} />
                      <F label="End date" type="date" v={t.end_date || ""} on={(v) => updateTerm(i, "end_date", v)} />
                    </div>
                    <F label="Institution (e.g. Starling / SumUp)" v={t.bank_name || ""} on={(v) => updateTerm(i, "bank_name", v)} />
                    <F label="Account holder" v={t.bank_account_name || ""} on={(v) => updateTerm(i, "bank_account_name", v)} />
                    <F label="Account number" v={t.bank_account_number || ""} on={(v) => updateTerm(i, "bank_account_number", v)} />
                    <F label="Sort code" v={t.bank_sort_code || ""} on={(v) => updateTerm(i, "bank_sort_code", v)} />
                  </div>
                </div>
              ))}
              {(!s.terms || s.terms.length === 0) && (
                <div className="text-sm text-[#6E6074] italic">No per-term overrides yet — the default account above will be used for every invoice.</div>
              )}
            </div>
          </div>
        )}

        {tab === "venue" && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm text-[#5D2C8F]">Venue address (used on newsletters & reminders)</label>
              <textarea rows={2} value={s.venue_address || ""} onChange={(e) => set("venue_address", e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="settings-venue" />
            </div>
            <F label="Signature name" v={s.signature_name} on={(v) => set("signature_name", v)} testid="settings-signature-name" />
            <F label="Sign-off phrase" v={s.signature_signoff} on={(v) => set("signature_signoff", v)} testid="settings-signature-signoff" hint='e.g. "Every Blessing", "Warmly", "Kind regards".' />
            <div className="md:col-span-2">
              <label className="text-sm text-[#5D2C8F]">Bank-details blurb (appears on invoices above the bank block)</label>
              <textarea rows={2} value={s.bank_details || ""} onChange={(e) => set("bank_details", e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="settings-bank-blurb" />
            </div>
            <div className="md:col-span-2 border-t border-[#E5DCE6] pt-4 mt-2">
              <h3 className="font-display text-lg text-[#5D2C8F]">WhatsApp group links</h3>
              <p className="text-xs text-[#6E6074] leading-snug">Paste each session's group invite link here (e.g. <code>https://chat.whatsapp.com/…</code>). When a <strong>new student</strong> receives their welcome pack or a bulk email with the WhatsApp toggle on, the matching link is pinned to the bottom. Leave blank to skip.</p>
            </div>
            <F label="Tuesday AM group" v={s.whatsapp_group_tue_am} on={(v) => set("whatsapp_group_tue_am", v)} testid="settings-wa-tue-am" />
            <F label="Tuesday PM group" v={s.whatsapp_group_tue_pm} on={(v) => set("whatsapp_group_tue_pm", v)} testid="settings-wa-tue-pm" />
            <F label="Friday Evening group" v={s.whatsapp_group_fri_eve} on={(v) => set("whatsapp_group_fri_eve", v)} testid="settings-wa-fri-eve" />
            <div className="md:col-span-2 border-t border-[#E5DCE6] pt-4 mt-2">
              <label className="text-sm text-[#5D2C8F]">Parking note (appears on every bulk email under the info block)</label>
              <textarea rows={3} value={s.parking_note || ""} onChange={(e) => set("parking_note", e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="settings-parking-note" />
            </div>
            <div className="md:col-span-2 border-t border-[#E5DCE6] pt-4 mt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!s.gallery_museum_frame}
                  onChange={(e) => set("gallery_museum_frame", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#5D2C8F]"
                  data-testid="settings-museum-frame-toggle"
                />
                <div>
                  <div className="text-sm font-medium text-[#5D2C8F]">Museum-style gallery frames</div>
                  <p className="text-xs text-[#6E6074] leading-snug mt-0.5">Display public gallery photos inside ornate gold frames with a mat, hanging from a ceiling rail — like paintings in a gallery. Turn it off any time to go back to the clean masonry layout.</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {tab === "contact" && (
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Public contact email" v={s.contact_email} on={(v) => set("contact_email", v)} testid="settings-email" />
            <F label="Public phone" v={s.contact_phone} on={(v) => set("contact_phone", v)} testid="settings-phone" />
            <div className="md:col-span-2">
              <label className="text-sm text-[#5D2C8F]">Public address (shown on Contact page)</label>
              <input value={s.contact_address || ""} onChange={(e) => set("contact_address", e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="settings-address" />
            </div>
            <div className="md:col-span-2 border-t border-[#E5DCE6] pt-4 mt-2">
              <h3 className="font-display text-lg text-[#5D2C8F]">Search engine verification</h3>
              <p className="text-xs text-[#6E6074] leading-snug">Paste the verification code Google (or Bing) gives you when you add this site to Search Console. They get injected as meta tags on every public page automatically — no need to edit HTML.</p>
            </div>
            <F label="Google Search Console token" v={s.google_site_verification} on={(v) => set("google_site_verification", v)} testid="settings-gsc" hint='In Search Console choose "HTML tag" verification and paste the content="…" value only, not the whole tag.' />
            <F label="Bing Webmaster token" v={s.bing_site_verification} on={(v) => set("bing_site_verification", v)} testid="settings-bing" hint="Optional — use the value from Bing Webmaster Tools' Meta-tag verification method." />
          </div>
        )}

        {tab === "social" && (
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Instagram handle" v={s.instagram_handle} on={(v) => set("instagram_handle", v)} testid="settings-ig" />
            <F label="Facebook handle" v={s.facebook_handle} on={(v) => set("facebook_handle", v)} testid="settings-fb" />
          </div>
        )}

        {tab === "lead_statuses" && <LeadStatusesManager />}

        {tab === "storage" && <StorageManager />}
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", testid, hint }) {
  return (
    <div>
      <label className="text-sm text-[#5D2C8F]">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid={testid} />
      {hint && <p className="text-xs text-[#8A7C92] mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

/**
 * Terms manager — list of named terms, only one marked active. The active
 * term's label feeds the legacy `settings.current_term` (mirrored in the
 * backend) so every existing consumer keeps working unchanged.
 */
function TermsManager() {
  const [rows, setRows] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/terms"); setRows(data || []); }
    catch { /* surface empty list */ }
  };
  React.useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newLabel.trim()) { toast.error("Give the term a name"); return; }
    try {
      await api.post("/admin/terms", { label: newLabel.trim(), start_date: newStart || null, end_date: newEnd || null });
      setNewLabel(""); setNewStart(""); setNewEnd("");
      toast.success("Term created ✓");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create term"); }
  };

  const patch = async (id, body) => {
    try { await api.patch(`/admin/terms/${id}`, body); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id, label) => {
    if (!confirm(`Delete the term "${label}"?`)) return;
    try { await api.delete(`/admin/terms/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  return (
    <div data-testid="terms-manager">
      <h3 className="font-display text-xl text-[#5D2C8F]">Terms</h3>
      <p className="text-xs text-[#6E6074] mt-0.5 mb-3 max-w-2xl leading-snug">
        Create future terms here so you can move students into them ahead of time. Mark one as <strong>active</strong> — that becomes the default term for new sign-ups and the one your stats &amp; "Current" tabs use. Dates are optional.
      </p>

      {/* Existing terms */}
      {rows.length === 0 ? (
        <div className="text-sm text-[#6E6074] bg-[#FAF6EE] rounded-lg p-5 text-center">No terms set yet — add your first one below.</div>
      ) : (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[#6E6074] uppercase tracking-wide border-b border-[#E5DCE6]">
                <th className="py-2 pr-3">Label</th>
                <th className="py-2 pr-3">Start</th>
                <th className="py-2 pr-3">End</th>
                <th className="py-2 pr-3">Active?</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className={`border-b border-[#F1ECE3] ${t.is_active ? "bg-[#FAF6EE]" : ""}`} data-testid={`term-row-${t.id}`}>
                  <td className="py-2 pr-3">
                    <input defaultValue={t.label} onBlur={(e) => e.target.value !== t.label && patch(t.id, { label: e.target.value })} className="bg-transparent border-b border-transparent hover:border-[#E5E0D8] focus:border-[#5D2C8F] outline-none text-[#5D2C8F] font-medium" data-testid={`term-label-${t.id}`} />
                  </td>
                  <td className="py-2 pr-3">
                    <input type="date" defaultValue={t.start_date || ""} onBlur={(e) => patch(t.id, { start_date: e.target.value || null })} className="bg-transparent border-b border-transparent hover:border-[#E5E0D8] focus:border-[#5D2C8F] outline-none text-xs" data-testid={`term-start-${t.id}`} />
                  </td>
                  <td className="py-2 pr-3">
                    <input type="date" defaultValue={t.end_date || ""} onBlur={(e) => patch(t.id, { end_date: e.target.value || null })} className="bg-transparent border-b border-transparent hover:border-[#E5E0D8] focus:border-[#5D2C8F] outline-none text-xs" data-testid={`term-end-${t.id}`} />
                  </td>
                  <td className="py-2 pr-3">
                    {t.is_active ? (
                      <span className="text-xs text-[#348469] inline-flex items-center gap-1"><Check size={11} /> Active</span>
                    ) : (
                      <button onClick={() => patch(t.id, { is_active: true })} className="text-xs text-[#5D2C8F] hover:underline" data-testid={`term-activate-${t.id}`}>Make active</button>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {!t.is_active && (
                      <button onClick={() => remove(t.id, t.label)} className="text-[#A685BD]" data-testid={`term-delete-${t.id}`}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add new */}
      <div className="grid sm:grid-cols-4 gap-3 items-end bg-[#FAF6EE] border border-[#E5E0D8] rounded-lg p-4">
        <div className="sm:col-span-2">
          <label className="text-xs text-[#5D2C8F] block mb-1">New term label</label>
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Autumn 2026" className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2 text-sm" data-testid="term-new-label" />
        </div>
        <div>
          <label className="text-xs text-[#5D2C8F] block mb-1">Start (optional)</label>
          <input type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2 text-sm" data-testid="term-new-start" />
        </div>
        <div>
          <label className="text-xs text-[#5D2C8F] block mb-1">End (optional)</label>
          <input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2 text-sm" data-testid="term-new-end" />
        </div>
        <button onClick={create} className="sac-btn-primary text-sm sm:col-span-4 justify-center" data-testid="term-create"><Plus size={14} /> Add term</button>
      </div>
    </div>
  );
}


/**
 * Lead-status catalogue manager. Backed by /api/admin/lead-statuses so
 * staff can add "Enrolled at other studio", "Ghosted after WhatsApp",
 * whatever wording matches their workflow — without me touching code.
 * Built-in keys (new/warm/hot/kit/dropped_out/student/returner/past_student/
 * closed) are label-editable but not deletable, so the Enrolled →
 * auto-convert-modal wiring keeps working.
 */
function LeadStatusesManager() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState({ label: "", color: "#5D2C8F" });
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/admin/lead-statuses").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const updateRow = async (row, patch) => {
    setSaving(row.key);
    try { await api.patch(`/admin/lead-statuses/${row.key}`, patch); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const order = rows.map((r) => r.key);
    [order[i], order[j]] = [order[j], order[i]];
    await api.post("/admin/lead-statuses/reorder", { order });
    await load();
  };

  const remove = async (row) => {
    if (row.is_builtin) return;
    if (!confirm(`Delete the "${row.label}" status? Any lead currently on it will be reset to "New".`)) return;
    await api.delete(`/admin/lead-statuses/${row.key}`);
    await load();
  };

  const addNew = async () => {
    const label = adding.label.trim();
    if (!label) { toast.error("Give the status a label first"); return; }
    try {
      await api.post("/admin/lead-statuses", { label, color: adding.color });
      setAdding({ label: "", color: "#5D2C8F" });
      toast.success("Status added");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  return (
    <div data-testid="lead-statuses-manager">
      <h3 className="font-display text-xl text-[#5D2C8F] mb-2">Lead statuses</h3>
      <p className="text-xs text-[#6E6074] mb-4 leading-snug">The labels + colours that appear in the Leads page status pill, edit form and filter chips. Built-in statuses (marked <span className="text-[#5D2C8F] font-semibold">locked</span>) can be renamed and recoloured but not deleted — the "Enrolled" one drives the auto-invoice modal. Custom statuses you add can be reordered and removed freely.</p>

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.key} className="flex items-center gap-2 bg-white border border-[#E5E0D8] rounded-lg p-2" data-testid={`lead-status-row-${r.key}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[#A685BD] disabled:opacity-30 text-[10px]" aria-label="Move up">▲</button>
              <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="text-[#A685BD] disabled:opacity-30 text-[10px]" aria-label="Move down">▼</button>
            </div>
            <input
              type="color" value={r.color || "#5D2C8F"}
              onChange={(e) => updateRow(r, { color: e.target.value })}
              className="w-8 h-8 rounded cursor-pointer border-0 p-0 bg-transparent"
              data-testid={`lead-status-color-${r.key}`}
            />
            <input
              type="text" defaultValue={r.label}
              onBlur={(e) => { if (e.target.value.trim() && e.target.value !== r.label) updateRow(r, { label: e.target.value.trim() }); }}
              className="flex-1 bg-transparent border-b border-transparent focus:border-[#A685BD] px-1 py-1 text-sm"
              data-testid={`lead-status-label-${r.key}`}
            />
            <span className="text-[10px] text-[#8A7C92] uppercase tracking-wider">{r.key}</span>
            {r.is_builtin ? (
              <span className="text-[10px] text-[#8A7C92] italic px-2">locked</span>
            ) : (
              <button onClick={() => remove(r)} className="p-1.5 text-[#A685BD] hover:text-red-500" data-testid={`lead-status-del-${r.key}`}><Trash2 size={14} /></button>
            )}
            {saving === r.key && <span className="text-[10px] text-[#8A7C92]">saving…</span>}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2 bg-[#FAF6EE] rounded-lg p-3">
        <input
          type="color" value={adding.color}
          onChange={(e) => setAdding({ ...adding, color: e.target.value })}
          className="w-8 h-8 rounded cursor-pointer border-0 p-0 bg-transparent"
          data-testid="lead-status-new-color"
        />
        <input
          type="text" value={adding.label}
          onChange={(e) => setAdding({ ...adding, label: e.target.value })}
          placeholder="New status label (e.g. 'Ghosted', 'Waitlist')"
          className="flex-1 bg-white border border-[#E5E0D8] rounded px-3 py-2 text-sm"
          data-testid="lead-status-new-label"
        />
        <button onClick={addNew} className="sac-btn-primary text-sm" data-testid="lead-status-add-btn"><Plus size={14} /> Add status</button>
      </div>
    </div>
  );
}

/**
 * StorageManager — owner-only pane on Settings → Storage.
 *
 * Surfaces the /api/admin/storage/status counts (per collection) and lets
 * the admin trigger the Object-Storage migration with two toggles:
 *   - `strip` (remove base64 from Mongo after upload — the payoff)
 *   - `force` (re-upload already-migrated docs)
 * The migration is idempotent and safe to run from production; a completed
 * run reports uploaded=0 on subsequent calls.
 */
function StorageManager() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [strip, setStrip] = useState(true);
  const [force, setForce] = useState(false);
  const [lastReport, setLastReport] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/storage/status");
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load storage status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const runMigration = async () => {
    const totalPending = status && Object.values(status.collections || {})
      .reduce((n, c) => n + (c.pending || 0), 0);
    if (!force && !totalPending) {
      if (!confirm("Nothing new to migrate. Run anyway (no-op)?")) return;
    } else {
      const msg = strip
        ? `About to migrate ${totalPending || "all"} document(s) to Object Storage and REMOVE the base64 field from Mongo. This is safe (public serving reads from storage_path). Continue?`
        : `About to migrate ${totalPending || "all"} document(s) to Object Storage (keeping base64 as fallback). Continue?`;
      if (!confirm(msg)) return;
    }
    setRunning(true);
    setLastReport(null);
    try {
      const params = [];
      if (strip) params.push("strip=true");
      if (force) params.push("force=true");
      const qs = params.length ? "?" + params.join("&") : "";
      const { data } = await api.post(`/admin/storage/migrate-blobs${qs}`);
      setLastReport(data);
      toast.success(`Migration complete — uploaded ${data.uploaded}, failed ${data.failed}`);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6E6074]">Loading storage status…</div>;
  if (!status) return null;

  const totals = Object.values(status.collections || {}).reduce(
    (acc, c) => ({
      total: acc.total + (c.total || 0),
      migrated: acc.migrated + (c.migrated || 0),
      pending: acc.pending + (c.pending || 0),
    }),
    { total: 0, migrated: 0, pending: 0 },
  );

  return (
    <div data-testid="storage-manager">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl text-[#5D2C8F] mb-1">Object Storage migration</h3>
          <p className="text-sm text-[#6E6074] max-w-xl">
            Moves curriculum PDFs and welcome-pack images out of MongoDB and into Emergent Object Storage. Reclaims RAM and stops Cloudflare 520 errors on big attachments. Safe, idempotent, and can be re-run at any time.
          </p>
        </div>
        <button onClick={loadStatus} className="text-xs text-[#5D2C8F] hover:underline inline-flex items-center gap-1" data-testid="storage-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {!status.configured && (
        <div className="mb-4 bg-[#FDF6E3] border border-[#E8C37A] text-[#8F6B1F] rounded-lg p-3 text-sm">
          <strong>EMERGENT_LLM_KEY is not configured</strong> — Object Storage is disabled until the key is added to backend env.
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <div className="admin-surface p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#8A7C92]">Docs total</div>
          <div className="font-display text-3xl text-[#5D2C8F]">{totals.total}</div>
        </div>
        <div className="admin-surface p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#8A7C92]">Migrated</div>
          <div className="font-display text-3xl text-[#1E5F5A]">{totals.migrated}</div>
        </div>
        <div className="admin-surface p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#8A7C92]">Pending</div>
          <div className="font-display text-3xl text-[#8F6B1F]">{totals.pending}</div>
        </div>
        <div className="admin-surface p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#8A7C92]">Configured</div>
          <div className="font-display text-3xl">{status.configured ? "✓" : "✗"}</div>
        </div>
      </div>

      <div className="admin-surface p-4 mb-5" data-testid="storage-collections">
        <table className="w-full text-sm">
          <thead className="text-left text-[#5D2C8F]">
            <tr>
              <th className="pb-2">Collection</th>
              <th className="pb-2 text-right">Total</th>
              <th className="pb-2 text-right">Migrated</th>
              <th className="pb-2 text-right">Pending</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(status.collections || {}).map(([name, c]) => (
              <tr key={name} className="border-t border-[#E5E0D8]" data-testid={`storage-row-${name}`}>
                <td className="py-2 font-mono text-xs">{name}</td>
                <td className="py-2 text-right">{c.total}</td>
                <td className="py-2 text-right text-[#1E5F5A]">{c.migrated}</td>
                <td className="py-2 text-right text-[#8F6B1F]">{c.pending}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-surface p-4 space-y-3">
        <h4 className="font-display text-lg text-[#5D2C8F]">Run migration</h4>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={strip} onChange={(e) => setStrip(e.target.checked)} data-testid="storage-strip-toggle" />
          <span>
            <strong>Strip base64 after upload</strong> <span className="text-[#6E6074]">— removes the heavy field from Mongo once storage has the bytes. Public serving still works via storage_path.</span>
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="storage-force-toggle" />
          <span>
            <strong>Force re-upload</strong> <span className="text-[#6E6074]">— re-upload docs that already have storage_path (rarely needed).</span>
          </span>
        </label>
        <button
          onClick={runMigration}
          disabled={running || !status.configured}
          className="sac-btn-primary disabled:opacity-50"
          data-testid="storage-run-migration"
        >
          <HardDrive size={14} /> {running ? "Migrating…" : "Run migration"}
        </button>
        {lastReport && (
          <div className="mt-3 bg-[#FAF6EE] rounded p-3 text-sm" data-testid="storage-last-report">
            <div className="font-medium text-[#5D2C8F] mb-1">Last run</div>
            <div>Uploaded: <strong>{lastReport.uploaded}</strong> · Skipped: {lastReport.skipped} · Failed: {lastReport.failed}</div>
            <div className="mt-1 text-xs text-[#6E6074]">Per-collection: {Object.entries(lastReport.collections || {}).map(([k, v]) => `${k}=${v.uploaded}`).join(", ")}</div>
          </div>
        )}
      </div>
    </div>
  );
}

